import type { DateRange } from "@repo/app-types/entities/filters";
import type { CustomerExperienceTrendData } from "@repo/app-types/entities/topic";
import { useMemo, type FC } from "react";
import { customerExperienceScoreToPercent } from "../topicStatsFormatters.js";
import { MetricTrendCard, type MetricTrendPoint } from "./metricTrendCard.js";

interface CustomerExperienceTrendCardProps {
  data: CustomerExperienceTrendData | undefined;
  isLoading: boolean;
  dateRange: DateRange;
}

function formatScore(score: number | null): string {
  if (score === null) {
    return "—";
  }
  return Math.round(score).toString();
}

function toPercent(score: number | null): number | null {
  return score === null ? null : customerExperienceScoreToPercent(score);
}

/**
 * Topics overview Customer Experience card. Renders the
 * weighted-average CX score for the visible date range on a 0-100
 * scale, a delta chip vs the immediately preceding equal-length
 * window, and a 7-day rolling sparkline. Raw per-grading 1-5 scores
 * (see {@link CustomerExperienceScoreAggregate}) are mapped via
 * {@link customerExperienceScoreToPercent}; visual structure lives in
 * the shared {@link MetricTrendCard}.
 */
export const CustomerExperienceTrendCard: FC<
  CustomerExperienceTrendCardProps
> = ({ data, isLoading, dateRange }) => {
  const { points, currentScore, priorScore, hasData } = useMemo(() => {
    if (!data) {
      return {
        points: [] as MetricTrendPoint[],
        currentScore: null,
        priorScore: null,
        hasData: false,
      };
    }
    const pts: MetricTrendPoint[] = data.buckets.map((b) => ({
      date: b.date,
      value: toPercent(b.rollingAverageScore),
    }));
    return {
      points: pts,
      currentScore: toPercent(data.currentPeriodAverageScore),
      priorScore: toPercent(data.priorPeriodAverageScore),
      hasData: data.currentPeriodAverageScore !== null,
    };
  }, [data]);

  const diff =
    currentScore !== null && priorScore !== null
      ? Math.round(currentScore - priorScore)
      : null;

  return (
    <MetricTrendCard
      label="Overall customer experience score"
      headlineValue={formatScore(currentScore)}
      diff={diff}
      formatDelta={(d) => `${d > 0 ? "+" : ""}${d}pts`}
      points={points}
      yDomain={[0, 100]}
      formatTooltipValue={formatScore}
      isLoading={isLoading && !data}
      hasNoData={!hasData}
      dateRange={dateRange}
    />
  );
};
